// Per-district STV results display (Stage 3C).
//
// Renders three things:
//   1. Header row: district label, status, "Count N of ~M · seats X/5".
//   2. Five-seats-filling strip — five slots, one per elected seat,
//      coloured by the winner's party, in election order.
//   3. Round-by-round table — candidate, party, per-round running
//      totals, status. Quota row pinned above the data so readers can
//      see "X over quota" at a glance.
//
// Both Layer 0 (first-count totals) and Layer 1 (running totals +
// status each round) are covered. Layer 2 (transfer-flow diagrams) is
// a later stretch.

import { useMemo } from 'react';
import {
  deriveDistrictCount,
  SEATS_PER_DISTRICT,
  type DerivedDistrict,
} from '../../data/electionday-results-types';
import { useResultsRoster, partyColour, partyChip, type RosterCandidate } from './useResultsRoster';
import { useResultsData } from './useResultsData';
import { formatDistrictLocalities } from '../../lib/districtLocalities';
import './Results.css';

interface Props {
  districtId: number;
  /** Compact mode for the homepage mount (drops the round table). */
  compact?: boolean;
}

export default function DistrictResultsCard({ districtId, compact = false }: Props) {
  const { data, isDemo } = useResultsData();
  const { byDistrict, byId } = useResultsRoster();

  const candidatesForDistrict = byDistrict(districtId);
  const candidateIds = useMemo(
    () => candidatesForDistrict.map((c) => c.id),
    [candidatesForDistrict],
  );
  const stored = data.districts[String(districtId)];
  const derived = useMemo(
    () => deriveDistrictCount(districtId, stored, candidateIds),
    [districtId, stored, candidateIds],
  );

  const hasData =
    derived.validVotes !== null || derived.rounds.length > 0;

  return (
    <section
      className="results-card"
      aria-label={`District ${districtId} STV count`}
    >
      {isDemo && (
        <p className="results-demo-inline">⚠ Demo / Test Data</p>
      )}
      <Header districtId={districtId} derived={derived} hasData={hasData} />
      <SeatStrip
        derived={derived}
        roster={candidatesForDistrict}
        byId={byId}
      />
      {hasData && !compact && (
        <RoundTable derived={derived} roster={candidatesForDistrict} />
      )}
      {hasData && derived.rounds.length > 0 && (
        <Caption derived={derived} byId={byId} />
      )}
    </section>
  );
}

// ── Header ──────────────────────────────────────────────────────────

function Header({
  districtId,
  derived,
  hasData,
}: {
  districtId: number;
  derived: DerivedDistrict;
  hasData: boolean;
}) {
  const filled = derived.electedSeats.length;
  const roundLabel =
    derived.rounds.length === 0
      ? hasData
        ? 'Valid votes recorded · count not started'
        : 'Awaiting count'
      : `Count ${derived.rounds[derived.rounds.length - 1].roundNumber} · seats ${filled} of ${SEATS_PER_DISTRICT}`;
  return (
    <header>
      <p className="results-card__kicker">District {districtId}</p>
      <h3 className="results-card__title">{districtLabel(districtId)}</h3>
      <p className="results-card__sub">
        {roundLabel}
        {derived.quota !== null && (
          <>
            {' · '}
            Quota <strong>{derived.quota.toLocaleString()}</strong>
            {derived.validVotes !== null && (
              <> · valid votes {derived.validVotes.toLocaleString()}</>
            )}
          </>
        )}
      </p>
    </header>
  );
}

// ── Seat strip — five slots, filled in election order ──────────────

function SeatStrip({
  derived,
  roster,
  byId,
}: {
  derived: DerivedDistrict;
  roster: RosterCandidate[];
  byId: (id: string) => RosterCandidate | undefined;
}) {
  const slots: Array<{ idx: number; cand: RosterCandidate | null; kind: 'quota' | 'without_quota' | null }> = [];
  for (let i = 0; i < SEATS_PER_DISTRICT; i++) {
    const id = derived.electedSeats[i];
    if (id) {
      const cand = byId(id) ?? roster.find((c) => c.id === id) ?? null;
      // Derive the kind by walking the rounds for this id.
      const kind = electionKindFor(id, derived);
      slots.push({ idx: i, cand, kind });
    } else {
      slots.push({ idx: i, cand: null, kind: null });
    }
  }

  return (
    <ol className="seat-strip" aria-label="Seats filled">
      {slots.map((s) => {
        const colour = s.cand ? partyColour(s.cand.party) : '';
        const filled = !!s.cand;
        return (
          <li
            key={s.idx}
            className={`seat-slot${filled ? ' seat-slot--filled' : ''}`}
            style={
              filled
                ? { background: colour, borderColor: colour }
                : undefined
            }
            // Keep candidateId on the DOM so a later candidate-face
            // enhancement can drop a photo in without changing the
            // data shape.
            data-candidate-id={s.cand?.id ?? ''}
          >
            <span className="seat-slot__index">#{s.idx + 1}</span>
            {s.cand ? (
              <>
                <p className="seat-slot__name">{s.cand.name}</p>
                <p className="seat-slot__party">{partyChip(s.cand.party)}</p>
                <p className="seat-slot__kind">
                  {s.kind === 'without_quota' ? 'elected without quota' : 'elected by quota'}
                </p>
              </>
            ) : (
              <p className="seat-slot__name" style={{ color: 'var(--muted, #999)' }}>
                —
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function electionKindFor(
  candidateId: string,
  derived: DerivedDistrict,
): 'quota' | 'without_quota' | null {
  for (const r of derived.rounds) {
    const row = r.perCandidate.find((p) => p.candidateId === candidateId);
    if (!row) continue;
    if (row.status === 'elected_by_quota') return 'quota';
    if (row.status === 'elected_without_quota') return 'without_quota';
  }
  return null;
}

// ── Round-by-round table ──────────────────────────────────────────

function RoundTable({
  derived,
  roster,
}: {
  derived: DerivedDistrict;
  roster: RosterCandidate[];
}) {
  // Row order: stable alpha-by-surname from the roster; the running-
  // total ordering across rounds is visible in the cells themselves.
  // We exclude rows that never appear (no entry in any round) — those
  // are candidates from the OTHER district list for someone who
  // didn't actually contest here.
  const rowIds = useMemo(() => {
    const seen = new Set<string>();
    for (const r of derived.rounds) {
      for (const p of r.perCandidate) seen.add(p.candidateId);
    }
    return roster.filter((c) => seen.has(c.id));
  }, [roster, derived]);

  if (rowIds.length === 0) {
    return <p className="results-empty">No round data yet.</p>;
  }

  return (
    <div className="round-table-wrap">
      <table className="round-table">
        <thead>
          <tr>
            <th>Candidate</th>
            {derived.rounds.map((r) => (
              <th key={r.roundNumber}>R{r.roundNumber}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {derived.quota !== null && (
            <tr className="round-table__quota-row">
              <td>Quota</td>
              {derived.rounds.map((r) => (
                <td key={r.roundNumber}>{derived.quota?.toLocaleString()}</td>
              ))}
            </tr>
          )}
          {rowIds.map((cand) => {
            const lastRow = lastEntryFor(cand.id, derived);
            const cls =
              lastRow?.status === 'excluded'
                ? 'is-excluded'
                : lastRow?.status === 'elected_by_quota' || lastRow?.status === 'elected_without_quota'
                  ? 'is-elected'
                  : '';
            return (
              <tr key={cand.id} className={cls}>
                <td>
                  <span className="round-table__name">
                    <span
                      className="round-table__party-dot"
                      style={{ background: partyColour(cand.party) }}
                      aria-hidden="true"
                    />
                    <span>
                      {cand.name}{' '}
                      <span style={{ color: 'var(--muted)', fontSize: 11 }}>
                        ({partyChip(cand.party)})
                      </span>
                    </span>
                  </span>
                </td>
                {derived.rounds.map((r) => {
                  const row = r.perCandidate.find((p) => p.candidateId === cand.id);
                  return (
                    <td key={r.roundNumber}>
                      {row ? row.runningTotal.toLocaleString() : '—'}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr>
            <td style={{ fontStyle: 'italic', color: 'var(--muted)' }}>
              Non-transferable
            </td>
            {derived.rounds.map((r) => (
              <td key={r.roundNumber} style={{ color: 'var(--muted)' }}>
                {r.nonTransferable.toLocaleString()}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function lastEntryFor(id: string, derived: DerivedDistrict):
  | { status: import('../../data/electionday-results-types').CandidateStatus }
  | undefined {
  for (let i = derived.rounds.length - 1; i >= 0; i--) {
    const row = derived.rounds[i].perCandidate.find((p) => p.candidateId === id);
    if (row) return { status: row.status };
  }
  return undefined;
}

// ── Caption — derived round label ─────────────────────────────────

function Caption({
  derived,
  byId,
}: {
  derived: DerivedDistrict;
  byId: (id: string) => RosterCandidate | undefined;
}) {
  const last = derived.rounds[derived.rounds.length - 1];
  const actor = last.actorId ? byId(last.actorId) : null;
  let text: string;
  switch (last.type) {
    case 'first_count':
      text = `Count ${last.roundNumber} — First-count totals`;
      break;
    case 'fill':
      text = `Count ${last.roundNumber} — Final seats decided`;
      break;
    case 'exclusion':
      text = actor
        ? `Count ${last.roundNumber} — ${actor.name} excluded; votes distributed`
        : `Count ${last.roundNumber} — Exclusion + transfer`;
      break;
    case 'surplus_distribution':
    default:
      text = actor
        ? `Count ${last.roundNumber} — Surplus from ${actor.name} distributed`
        : `Count ${last.roundNumber} — Surplus distribution`;
      break;
  }
  return <p className="results-card__caption">{text}</p>;
}

// ── District label helper ─────────────────────────────────────────
// Sourced from malta_districts_2022.geojson properties.localities
// via the shared formatDistrictLocalities (imported at the top of
// the file). Truncate Gozo's 14 to first 4.

function districtLabel(districtId: number): string {
  return formatDistrictLocalities(districtId, 4);
}
